import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, post } from '@aiia/portal-core';
import { useWidgets, useApiKeys } from '../hooks';
import { getPrompts } from '../features/api/prompts';
import { getTools } from '../features/api/tools';
import type { UserTool } from '../features/api/tools';
import DemoChat from '../components/DemoChat';

const AGENT_URL = import.meta.env.VITE_AGENT_URL || 'http://localhost:8081';

interface EndpointDoc {
  method: string;
  path: string;
  description: string;
  sampleResponse: string;
}

interface ScenarioConfig {
  id: string;
  icon: string;
  titleKey: string;
  descriptionKey: string;
  widgetNamePattern: string;
  suggestedMessages: string[];
}

const ENDPOINT_DOCS: Record<string, EndpointDoc[]> = {
  ecommerce: [
    {
      method: 'GET',
      path: '/products?search={query}',
      description: 'პროდუქტების ძიება სახელით, ბრენდით ან კატეგორიით. search პარამეტრი არასავალდებულოა — ცარიელი მოთხოვნა ყველა პროდუქტს აბრუნებს.',
      sampleResponse: `{
  "products": [
    {
      "product_id": "PRD-001",
      "name": "iPhone 15 Pro",
      "category": "სმარტფონები",
      "brand": "Apple",
      "price": 3299.0,
      "rating": 4.8
    }
  ],
  "total": 1
}`,
    },
    {
      method: 'GET',
      path: '/products/{product_id}',
      description: 'კონკრეტული პროდუქტის სრული ინფორმაცია ID-ით.',
      sampleResponse: `{
  "product_id": "PRD-001",
  "name": "iPhone 15 Pro",
  "category": "სმარტფონები",
  "brand": "Apple",
  "price": 3299.0,
  "description": "Apple iPhone 15 Pro 256GB...",
  "rating": 4.8,
  "reviews_count": 142
}`,
    },
    {
      method: 'GET',
      path: '/products/{product_id}/stock',
      description: 'პროდუქტის მარაგის შემოწმება — რაოდენობა და საწყობის მდებარეობა.',
      sampleResponse: `{
  "product_id": "PRD-001",
  "მარაგშია": true,
  "რაოდენობა": 23,
  "საწყობი": "თბილისი"
}`,
    },
    {
      method: 'POST',
      path: '/orders',
      description: 'შეკვეთის გაფორმება. Body: customer_name, phone, items (JSON array). ჯამი ავტომატურად გამოითვლება. სტატუსი იცვლება დროში: მუშავდება → გაგზავნილია → მიწოდებულია.',
      sampleResponse: `// Request Body:
{
  "customer_name": "გიორგი",
  "phone": "+995555123456",
  "items": [{"product_id": "PRD-001", "quantity": 1}]
}
// Response:
{
  "order_id": "ORD-2001",
  "status": "მუშავდება",
  "items": [...],
  "total": 3299.0,
  "message": "შეკვეთა წარმატებით მიიღება!"
}`,
    },
    {
      method: 'GET',
      path: '/orders/{order_id}',
      description: 'შეკვეთის სტატუსი. დინამიური შეკვეთებისთვის სტატუსი დროში იცვლება: <60წმ მუშავდება, 60-120წმ გაგზავნილია, >120წმ მიწოდებულია.',
      sampleResponse: `{
  "order_id": "ORD-1001",
  "customer_name": "გიორგი კვარაცხელია",
  "status": "მიწოდებულია",
  "items": [...],
  "total": 3299.0
}`,
    },
    {
      method: 'GET',
      path: '/customers/{customer_id}',
      description: 'მომხმარებლის პროფილის ინფორმაცია — სახელი, ტელეფონი, ქალაქი, შეკვეთების რაოდენობა.',
      sampleResponse: `{
  "customer_id": "CUS-001",
  "name": "გიორგი კვარაცხელია",
  "email": "giorgi@example.com",
  "phone": "+995 555 123456",
  "city": "თბილისი",
  "orders_count": 5,
  "total_spent": 12450.0
}`,
    },
    {
      method: 'POST',
      path: '/tickets',
      description: 'მხარდაჭერის ტიკეტის შექმნა. Body: customer_id, subject, message. აბრუნებს ტიკეტის ID-ს და სავარაუდო პასუხის დროს.',
      sampleResponse: `{
  "ticket_id": "TKT-4521",
  "status": "გახსნილია",
  "customer_id": "CUS-001",
  "subject": "დეფექტური პროდუქტი",
  "estimated_response": "24 საათი"
}`,
    },
  ],
  clinic: [
    {
      method: 'GET',
      path: '/doctors?specialty={specialty}',
      description: 'ექიმების სია ფილტრით სპეციალობის მიხედვით. specialty არასავალდებულოა.',
      sampleResponse: `{
  "doctors": [
    {
      "doctor_id": "DOC-001",
      "name": "დავით მაისურაძე",
      "specialty": "ოჯახის ექიმი",
      "experience_years": 18,
      "rating": 4.9,
      "consultation_fee_gel": 50
    }
  ],
  "total": 1
}`,
    },
    {
      method: 'GET',
      path: '/doctors/{doctor_id}/schedule?date={YYYY-MM-DD}',
      description: 'ექიმის განრიგი კონკრეტული თარიღისთვის. აბრუნებს სლოტებს (09:00-17:00) თავისუფალი/დაკავებული სტატუსით. შაბათ-კვირას კლინიკა არ მუშაობს.',
      sampleResponse: `{
  "doctor_id": "DOC-001",
  "doctor_name": "დავით მაისურაძე",
  "date": "2026-03-10",
  "day": "სამშაბათი",
  "consultation_fee_gel": 50,
  "slots": [
    {"time": "09:00", "available": true, "status": "თავისუფალი"},
    {"time": "10:00", "available": false, "status": "დაკავებული"},
    ...
  ]
}`,
    },
    {
      method: 'POST',
      path: '/appointments',
      description: 'ვიზიტის დაჯავშნა. Body: patient_name, phone, doctor_id, date, time. თუ სლოტი დაკავებულია, აბრუნებს 409 + ალტერნატიული სლოტების სიას.',
      sampleResponse: `// წარმატებული (200):
{
  "appointment_id": "APT-1001",
  "status": "დადასტურებული",
  "doctor_name": "ნინო ჩხეიძე",
  "date": "2026-03-10",
  "time": "14:00",
  "reminder": "შეხსენება გამოიგზავნება 1 საათით ადრე"
}
// დაკავებული (409):
{
  "error": "ეს დრო უკვე დაკავებულია",
  "available_alternatives": ["11:00", "14:00", "16:00"]
}`,
    },
    {
      method: 'DELETE',
      path: '/appointments/{appointment_id}',
      description: 'ვიზიტის გაუქმება. აბრუნებს გაუქმებული ვიზიტის დეტალებს.',
      sampleResponse: `{
  "appointment_id": "APT-1001",
  "status": "გაუქმებული",
  "doctor_name": "ნინო ჩხეიძე",
  "date": "2026-03-10",
  "time": "14:00",
  "message": "ვიზიტი წარმატებით გაუქმდა"
}`,
    },
    {
      method: 'GET',
      path: '/services',
      description: 'კლინიკის ყველა მომსახურების სია ფასებით და ხანგრძლივობით.',
      sampleResponse: `{
  "services": [
    {
      "service_id": "SRV-001",
      "name": "საერთო კონსულტაცია",
      "price_gel": 50,
      "duration_min": 30
    },
    ...
  ],
  "total": 9,
  "currency": "GEL"
}`,
    },
    {
      method: 'GET',
      path: '/patients/{phone}/history',
      description: 'პაციენტის ვიზიტების ისტორია ტელეფონის ნომრით. აბრუნებს წინა ვიზიტებს, დიაგნოზებს და შენიშვნებს.',
      sampleResponse: `{
  "phone": "555123456",
  "visits": [
    {
      "date": "2026-01-15",
      "doctor": "ნინო ჩხეიძე",
      "specialty": "კარდიოლოგი",
      "service": "ექოკარდიოგრაფია",
      "diagnosis": "ნორმა"
    }
  ],
  "total_visits": 3
}`,
    },
  ],
  restaurant: [
    {
      method: 'GET',
      path: '/menu?category={category}',
      description: 'მენიუს ჩვენება. category ფილტრი არასავალდებულოა. კატეგორიები: ქართული, პიცა, სალათები, სასმელები, დესერტი.',
      sampleResponse: `[
  {
    "item_id": "FOOD-001",
    "name": "ხინკალი",
    "category": "ქართული",
    "price": 1.2,
    "prep_time_min": 25,
    "popular": true
  },
  ...
]`,
    },
    {
      method: 'GET',
      path: '/menu/search?query={query}',
      description: 'მენიუში ძიება სახელით.',
      sampleResponse: `{
  "query": "ხინკალი",
  "results": [
    {"item_id": "FOOD-001", "name": "ხინკალი", "price": 1.2, ...}
  ],
  "count": 1
}`,
    },
    {
      method: 'POST',
      path: '/orders',
      description: 'შეკვეთის გაფორმება. Body: customer_name, phone, address, items. ფასები ავტომატურად გამოითვლება + მიწოდების საკომისიო. სტატუსი იცვლება: მიღებულია → მზადდება → გზაშია → მიწოდებულია.',
      sampleResponse: `// Request Body:
{
  "customer_name": "გიორგი",
  "phone": "555123456",
  "address": "ვაკე, ჭავჭავაძის 10",
  "items": [{"name": "ხინკალი", "quantity": 20}]
}
// Response:
{
  "order_id": "ORD-5001",
  "status": "მიღებულია",
  "items": [{"name": "ხინკალი", "quantity": 20, "unit_price": 1.2, "item_total": 24.0}],
  "subtotal": 24.0,
  "delivery_fee": 5.0,
  "total": 29.0,
  "estimated_delivery": "25-35 წუთი"
}`,
    },
    {
      method: 'GET',
      path: '/orders/{order_id}/status',
      description: 'შეკვეთის სტატუსის თვალყურის დევნება. სტატუსი დროში იცვლება: <30წმ მიღებულია, 30-60წმ მზადდება, 60-120წმ გზაშია, >120წმ მიწოდებულია.',
      sampleResponse: `{
  "order_id": "ORD-5001",
  "status": "მზადდება",
  "status_message": "თქვენი შეკვეთა მზადდება სამზარეულოში",
  "items": [...],
  "total": 29.0,
  "estimated_delivery": "25-35 წუთი"
}`,
    },
    {
      method: 'GET',
      path: '/delivery/estimate?address={address}',
      description: 'მიწოდების დროის შეფასება მისამართით. რაიონები: ვაკე 25წთ, საბურთალო 30წთ, დიდუბე 35წთ, ისანი 40წთ, გლდანი 45წთ, მთაწმინდა 20წთ.',
      sampleResponse: `{
  "address": "ვაკე",
  "estimated_minutes": 25,
  "delivery_fee": 5.0,
  "message": "სავარაუდო მიტანის დრო: 25-35 წუთი"
}`,
    },
    {
      method: 'GET',
      path: '/promotions',
      description: 'მიმდინარე აქციების სია ფასდაკლებებით და მოქმედების ვადით.',
      sampleResponse: `[
  {
    "promo_id": "PROMO-001",
    "title": "ოჯახური კომბო",
    "description": "ხინკალი 10 ცალი + ხაჭაპური აჭარული + 2 ლიმონათი",
    "original_price": 37.0,
    "promo_price": 28.0,
    "valid_until": "2026-03-31"
  }
]`,
    },
  ],
};

const SCENARIOS: ScenarioConfig[] = [
  {
    id: 'ecommerce',
    icon: '\uD83D\uDED2',
    titleKey: 'examples.ecommerce.title',
    descriptionKey: 'examples.ecommerce.description',
    widgetNamePattern: 'Demo: E-commerce',
    suggestedMessages: [
      'რა პროდუქტები გაქვთ?',
      'iPhone 15 Pro რა ღირს და მარაგშია?',
      'მინდა შევუკვეთო iPhone 15 Pro და AirPods Pro. რა დამიჯდება?',
      'დავადასტუროთ შეკვეთა, გიორგი, +995555123456',
      'შემიმოწმე ეს შეკვეთა',
    ],
  },
  {
    id: 'clinic',
    icon: '\uD83C\uDFE5',
    titleKey: 'examples.clinic.title',
    descriptionKey: 'examples.clinic.description',
    widgetNamePattern: 'Demo: Clinic',
    suggestedMessages: [
      'რა ექიმები გყავთ?',
      'კარდიოლოგთან ვიზიტი მინდა, ვინ არის და რა ღირს?',
      'რა დროები აქვს თავისუფალი ორშაბათს?',
      'დამიჯავშნეთ 14:00-ზე, ნინო, +995555111222',
      'რა მომსახურებები გაქვთ და რა ღირს?',
    ],
  },
  {
    id: 'restaurant',
    icon: '\uD83C\uDF55',
    titleKey: 'examples.restaurant.title',
    descriptionKey: 'examples.restaurant.description',
    widgetNamePattern: 'Demo: Restaurant',
    suggestedMessages: [
      'რა მენიუ გაქვთ?',
      'ხინკალი რა ღირს და ქაბაბი?',
      'მინდა 20 ხინკალი და 10 ქაბაბი. ვაკეში მიმიტანეთ.',
      'შემიმოწმეთ ჩემი შეკვეთა',
      'რა აქციები გაქვთ?',
    ],
  },
];

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-100 text-green-700',
  POST: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
  PUT: 'bg-yellow-100 text-yellow-700',
};

function ApiDocsModal({ scenarioId, onClose }: { scenarioId: string; onClose: () => void }) {
  const endpoints = ENDPOINT_DOCS[scenarioId] || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">API Endpoints</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              თქვენს რეალურ სერვისში ანალოგიური ენდპოინტები უნდა გქონდეთ. აქ ნაჩვენებია სადემონსტრაციო (mock) API.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {endpoints.map((ep, i) => (
            <div key={i} className="border rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 p-3 bg-gray-50 border-b">
                <span className={`px-2 py-0.5 text-xs font-mono font-bold rounded ${METHOD_COLORS[ep.method] || 'bg-gray-100'}`}>
                  {ep.method}
                </span>
                <code className="text-sm font-semibold text-gray-800">{ep.path}</code>
              </div>
              <div className="p-3 space-y-2">
                <p className="text-sm text-gray-700">{ep.description}</p>
                <details className="group">
                  <summary className="text-xs font-medium text-indigo-600 cursor-pointer hover:text-indigo-800">
                    Response
                  </summary>
                  <pre className="mt-2 bg-gray-900 text-green-300 text-xs p-3 rounded-lg overflow-x-auto whitespace-pre">
                    {ep.sampleResponse}
                  </pre>
                </details>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t bg-gray-50 rounded-b-xl">
          <p className="text-xs text-gray-500">
            ეს არის სადემო ენდპოინტები. თქვენს რეალურ აგენტში, შეცვალეთ Webhook URL ინსტრუმენტების კონფიგურაციაში
            და მიუთითეთ თქვენი სერვისის მისამართი (მაგ. https://your-api.com/products).
          </p>
        </div>
      </div>
    </div>
  );
}

function ToolCard({ tool }: { tool: UserTool }) {
  const method = tool.webhookMethod || 'GET';
  const requiredParams = tool.requiredParams || [];

  // Extract readable path from webhook URL
  const endpointPath = tool.webhookUrl
    ? tool.webhookUrl.replace(/^https?:\/\/[^/]+/, '').replace(/\$\{(\w+)\}/g, '{$1}')
    : '';

  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      {/* Tool name */}
      <div className="px-3 py-2 bg-gray-50 border-b flex items-center gap-2">
        <span className="font-semibold text-sm text-gray-900">{tool.toolName}</span>
      </div>

      <div className="p-3 space-y-3 text-sm">
        {/* Description */}
        <p className="text-xs text-gray-600 leading-relaxed">{tool.toolDescription}</p>

        {/* Webhook — form-like layout */}
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-3">
            <label className="block text-[10px] text-gray-400 uppercase mb-0.5">Method</label>
            <div className={`px-2 py-1 text-xs font-mono font-bold rounded text-center ${METHOD_COLORS[method] || 'bg-gray-100'}`}>
              {method}
            </div>
          </div>
          <div className="col-span-9">
            <label className="block text-[10px] text-gray-400 uppercase mb-0.5">Endpoint</label>
            <div className="px-2 py-1 bg-gray-50 border rounded text-xs font-mono text-gray-700 truncate" title={endpointPath}>
              {endpointPath || '-'}
            </div>
          </div>
        </div>

        {/* Parameters — table-like */}
        {tool.parametersSchema && Object.keys(tool.parametersSchema).length > 0 && (
          <div>
            <label className="block text-[10px] text-gray-400 uppercase mb-1">Parameters</label>
            <div className="border rounded divide-y">
              {Object.entries(tool.parametersSchema).map(([name, param]) => (
                <div key={name} className="flex items-center px-2 py-1.5 text-xs gap-2">
                  <code className="font-semibold text-gray-800 min-w-[80px]">{name}</code>
                  <span className="text-gray-400 text-[11px]">{param.type}</span>
                  {requiredParams.includes(name) ? (
                    <span className="text-[10px] bg-red-50 text-red-600 px-1.5 rounded font-medium">required</span>
                  ) : (
                    <span className="text-[10px] bg-gray-50 text-gray-400 px-1.5 rounded">optional</span>
                  )}
                  {param.description && (
                    <span className="text-gray-500 ml-auto text-[11px] truncate max-w-[120px]" title={param.description}>{param.description}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ExamplesPage() {
  const { t } = useTranslation();
  const [activeScenario, setActiveScenario] = useState<string>('ecommerce');
  const [instructionsCollapsed, setInstructionsCollapsed] = useState(false);
  const [apiDocsOpen, setApiDocsOpen] = useState(false);

  const { widgets, isLoading: widgetsLoading, refetch: refetchWidgets } = useWidgets();
  const { apiKeys, isLoading: apiKeysLoading, refetch: refetchApiKeys } = useApiKeys();
  const [provisioning, setProvisioning] = useState(false);

  const { data: allPrompts, refetch: refetchPrompts } = useQuery({
    queryKey: ['prompts'],
    queryFn: () => getPrompts(),
  });

  const { data: allTools, refetch: refetchTools } = useQuery({
    queryKey: ['tools'],
    queryFn: () => getTools(),
  });

  const scenario = SCENARIOS.find(s => s.id === activeScenario)!;

  // Find demo widgets by name
  const demoWidgets = useMemo(() => {
    if (!widgets) return {};
    const map: Record<string, (typeof widgets)[0]> = {};
    for (const w of widgets) {
      for (const s of SCENARIOS) {
        if (w.name === s.widgetNamePattern) {
          map[s.id] = w;
        }
      }
    }
    return map;
  }, [widgets]);

  const demoApiKey = useMemo(() => {
    if (!apiKeys) return null;
    return apiKeys.find(k => k.name === 'Demo API Key' && k.status === 'ACTIVE') || null;
  }, [apiKeys]);

  const activeWidget = demoWidgets[activeScenario];

  // Find prompt and tools for the active widget
  const activePrompt = useMemo(() => {
    if (!allPrompts || !activeWidget?.promptId) return null;
    return allPrompts.find(p => p.id === activeWidget.promptId) || null;
  }, [allPrompts, activeWidget?.promptId]);

  const activeTools = useMemo<UserTool[]>(() => {
    if (!allTools || !activeWidget?.toolIds) return [];
    return allTools.filter(t => activeWidget.toolIds!.includes(t.id));
  }, [allTools, activeWidget?.toolIds]);

  const isLoading = widgetsLoading || apiKeysLoading;
  const isReady = activeWidget && demoApiKey?.keyValue;

  const scenarioEndpoints = ENDPOINT_DOCS[activeScenario] || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('examples.title')}</h1>
        <p className="text-gray-600 mt-1">{t('examples.subtitle')}</p>
      </div>

      {/* Scenario Tabs — always visible */}
      <div className="flex gap-3 flex-wrap">
        {SCENARIOS.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveScenario(s.id)}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeScenario === s.id
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 border hover:border-indigo-300 hover:text-indigo-600'
            }`}
          >
            <span className="mr-2">{s.icon}</span>
            {t(s.titleKey)}
          </button>
        ))}
      </div>

      {/* Description */}
      <Card>
        <div className="p-5">
          <p className="text-gray-700">{t(scenario.descriptionKey)}</p>
        </div>
      </Card>

      {/* Loading state — only in content area, tabs stay */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{t('examples.setupInProgress')}</p>
          </div>
        </div>
      ) : (
        <div className={`grid grid-cols-1 ${instructionsCollapsed ? '' : 'lg:grid-cols-5'} gap-6`}>
          {/* Left: Instructions (collapsible) */}
          <div className={instructionsCollapsed ? '' : 'lg:col-span-2'}>
            <button
              onClick={() => setInstructionsCollapsed(!instructionsCollapsed)}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 mb-3"
            >
              <span>{instructionsCollapsed ? '\u25B6' : '\u25BC'}</span>
              {instructionsCollapsed ? t('examples.showInstructions') : t('examples.hideInstructions')}
            </button>

            {!instructionsCollapsed && (
              <div className="space-y-4">
                {/* System Prompt */}
                <Card>
                  <div className="p-4">
                    <h3 className="font-semibold mb-2">{t('examples.systemPrompt')}</h3>
                    <pre className="bg-gray-50 p-3 rounded text-sm whitespace-pre-wrap border text-gray-700">
                      {activePrompt?.systemPrompt || '...'}
                    </pre>
                    {activePrompt && (
                      <div className="mt-2 flex gap-2 text-xs text-gray-500">
                        <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded">Agent Mode</span>
                        <span className="bg-gray-100 px-2 py-0.5 rounded">Max iterations: {activePrompt.maxIterations}</span>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Tools — from DB or fallback to static endpoint docs */}
                <Card>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">
                        {t('examples.toolsList')}
                        {activeTools.length > 0 && <span className="text-gray-400 font-normal ml-2">({activeTools.length})</span>}
                      </h3>
                      <button
                        onClick={() => setApiDocsOpen(true)}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                      >
                        <span>API Endpoints</span>
                        <span>&rarr;</span>
                      </button>
                    </div>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                      {activeTools.length > 0 ? (
                        activeTools.map(tool => <ToolCard key={tool.id} tool={tool} />)
                      ) : (
                        /* Fallback: show endpoint docs in form-like style when tools aren't loaded */
                        scenarioEndpoints.map((ep, i) => (
                          <div key={i} className="border rounded-lg bg-white overflow-hidden">
                            <div className="px-3 py-2 bg-gray-50 border-b">
                              <span className="font-semibold text-sm text-gray-900">{ep.path.split('?')[0].split('/').pop()}</span>
                            </div>
                            <div className="p-3 space-y-2 text-sm">
                              <p className="text-xs text-gray-600 leading-relaxed">{ep.description}</p>
                              <div className="grid grid-cols-12 gap-2">
                                <div className="col-span-3">
                                  <label className="block text-[10px] text-gray-400 uppercase mb-0.5">Method</label>
                                  <div className={`px-2 py-1 text-xs font-mono font-bold rounded text-center ${METHOD_COLORS[ep.method] || 'bg-gray-100'}`}>
                                    {ep.method}
                                  </div>
                                </div>
                                <div className="col-span-9">
                                  <label className="block text-[10px] text-gray-400 uppercase mb-0.5">Endpoint</label>
                                  <div className="px-2 py-1 bg-gray-50 border rounded text-xs font-mono text-gray-700 truncate">
                                    {ep.path}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>

          {/* Right: Chat */}
          <div className={`${instructionsCollapsed ? '' : 'lg:col-span-3'} overflow-hidden`} style={{ height: 'calc(100vh - 280px)' }}>
            {isReady ? (
              <DemoChat
                key={activeScenario}
                widgetId={activeWidget.id}
                apiKey={demoApiKey!.keyValue!}
                agentUrl={AGENT_URL}
                suggestedMessages={scenario.suggestedMessages}
              />
            ) : (
              <Card>
                <div className="p-8 text-center space-y-4">
                  <div className="text-4xl">{scenario.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900">{t(scenario.titleKey)}</h3>
                  <p className="text-gray-500 text-sm">{t('examples.scenarioNotCreated')}</p>
                  <button
                    onClick={() => {
                      setProvisioning(true);
                      post<void>(`/users/me/demo-provision/${activeScenario}`, {})
                        .then(() => {
                          refetchWidgets();
                          refetchApiKeys();
                          refetchPrompts();
                          refetchTools();
                        })
                        .catch(() => {})
                        .finally(() => setProvisioning(false));
                    }}
                    disabled={provisioning}
                    className="px-5 py-2.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                  >
                    {provisioning ? t('examples.setupInProgress') : t('examples.createScenario')}
                  </button>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {apiDocsOpen && (
        <ApiDocsModal scenarioId={activeScenario} onClose={() => setApiDocsOpen(false)} />
      )}
    </div>
  );
}
